import { Icon, Progressbar, Title, Tooltip, Text, Flex } from '@upsales/components';
import { currencyFormat } from 'Components/Filters/Currencies';
import { useTranslation } from 'Components/Helpers/translate';
import { NumberFormat } from 'App/babel/utils/numberFormat';
import BemClass from '@upsales/components/Utils/bemClass';
import { Quota } from 'App/resources/Model/Client';
import openModal from 'App/services/Modal';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import moment from 'moment';
import React from 'react';

import './SalesTarget.scss';

type Props = {
	quotas: Quota[];
	quotasIncludingSubaccounts: Quota[];
	clientId: number;
	currentQuotaType: 'sales' | 'recurring' | 'contributionMargin';
	salesThisYear?: number;
	cmThisYear?: number;
	arrThisYear?: number;
	mrrThisYear?: number;
};

const SalesTarget = ({
	quotas,
	quotasIncludingSubaccounts,
	clientId,
	currentQuotaType,
	salesThisYear = 0,
	cmThisYear = 0,
	arrThisYear = 0,
	mrrThisYear = 0
}: Props) => {
	const { t } = useTranslation();
	const { metadata } = useSelector(({ App }: RootState) => App);
	const { SalesModelOption, SalesModel } = metadata?.params || {};
	const currency = metadata?.defaultCurrency.iso;
	const classes = new BemClass('SalesTarget');

	const hasRecurring = SalesModel === 'rr';
	const hasARR = hasRecurring && SalesModelOption === 'arr';
	const currencyQuota = quotas.find(q => q.type === currentQuotaType)?.currency ?? currency ?? 'SEK';

	const numberFormat = new NumberFormat();
	const currencyRate = metadata?.customerCurrencies.find(c => c.iso === currencyQuota)?.rate ?? 1;

	const currentQuota =
		((quotasIncludingSubaccounts?.length ?? 0) > 0 ? quotasIncludingSubaccounts : quotas)?.reduce(
			(res, current) => {
				if (moment().year() === moment(current.period).year() && current.type === currentQuotaType) {
					if (current.currency === currencyQuota) {
						return res + current.quota * current.currencyRate;
					} else {
						return res + current.quota;
					}
				}
				return res;
			},
			0
		) ?? 0;

	const getProgressValue = () => {
		let progress = 0;

		switch (currentQuotaType) {
			case 'sales':
				progress = salesThisYear;
				break;
			case 'recurring':
				progress = hasARR ? arrThisYear : mrrThisYear;
				break;
			case 'contributionMargin':
				progress = cmThisYear;
				break;
			default:
				progress = salesThisYear;
				break;
		}

		return (progress * currencyRate) / currentQuota;
	};

	const progress = getProgressValue();

	return (
		<Tooltip title={t('client.target.editCompanyTarget')}>
			<Flex
				space="ptm pbm prm plm"
				direction="column"
				onClick={() => openModal('EditClientTarget', { quotas, clientId })}
				gap="u2"
				className={classes.b()}
			>
				<Title>{currencyFormat(currentQuota, currencyQuota)}</Title>
				<Progressbar color="green" size="sm" value={currentQuota === 0 ? 0 : progress * 100} />
				<Flex justifyContent="space-between">
					<Text size="sm">
						{t(
							currentQuotaType === 'recurring'
								? 'client.target.percentOfARRTarget'
								: currentQuotaType === 'contributionMargin'
								? 'client.target.percentOfCMTarget'
								: 'client.target.percentOfTarget',
							{
								percent: numberFormat.percent(currentQuota === 0 ? 0 : progress)
							}
						)}
					</Text>
					<Icon name="cog" color="grey-8" />
				</Flex>
			</Flex>
		</Tooltip>
	);
};

export default SalesTarget;
